import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { vcollectProjectsApi } from '../../api/vcollect';
import { programmesApi } from '../../api/programmes';
import { projectsApi } from '../../api/projects';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';

export const VCOLLECT_STATUS_LABELS: Record<string, string> = { draft: 'Draft', active: 'Active', closed: 'Closed', archived: 'Archived' };

interface CreateForm {
  name: string; description?: string; programmeId?: number; projectId?: number; collectionStartDate?: string; collectionEndDate?: string;
}

export function VCollectProjectsListPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateForm>();

  const { data, isLoading, isError } = useQuery({ queryKey: ['vcollect-projects'], queryFn: () => vcollectProjectsApi.list() });
  const { data: programmesData } = useQuery({ queryKey: ['programmes'], queryFn: () => programmesApi.list() });
  const programmes = programmesData?.data ?? [];
  const { data: projectsData } = useQuery({ queryKey: ['projects'], queryFn: () => projectsApi.list() });
  const projects = projectsData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (input: CreateForm) => vcollectProjectsApi.create({ ...input, programmeId: input.programmeId ? Number(input.programmeId) : undefined, projectId: input.projectId ? Number(input.projectId) : undefined }),
    onSuccess: (result) => {
      showSuccess(`VCollect project ${result.data.reference} created.`);
      queryClient.invalidateQueries({ queryKey: ['vcollect-projects'] });
      setShowCreateForm(false);
      reset();
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const vcollectProjects = data?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="VCollect — Data Collection Projects" description="Organizational data collection exercises, built on the ODK ecosystem. Part of VConnect." />

      <SectionHeader title="Data Collection Projects" actions={
        <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'New Data Collection Project'}</button>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Name <input {...register('name', { required: true })} /></label>
          <label>Description <input {...register('description')} /></label>
          <label>Programme
            <select {...register('programmeId', { valueAsNumber: true })}>
              <option value="">None</option>
              {programmes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label>Linked Project
            <select {...register('projectId', { valueAsNumber: true })}>
              <option value="">None</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
            </select>
          </label>
          <label>Collection Start <input type="date" {...register('collectionStartDate')} /></label>
          <label>Collection End <input type="date" {...register('collectionEndDate')} /></label>
          <button type="submit" disabled={createMutation.isPending}>Create</button>
        </form>
      )}

      {isError && <div className="error-state">Failed to load data collection projects. Please try refreshing the page.</div>}
      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={vcollectProjects}
          rowKey={(p) => p.id}
          columns={[
            { header: 'Reference', render: (p) => <a onClick={() => navigate(`/vcollect/projects/${p.id}`)} className="table-link">{p.reference}</a> },
            { header: 'Name', render: (p) => p.name },
            { header: 'Programme', render: (p) => p.programme_name ?? '-' },
            { header: 'Linked Project', render: (p) => p.project_name ?? '-' },
            { header: 'Collection Period', render: (p) => p.collection_start_date ? `${formatDate(p.collection_start_date)} – ${p.collection_end_date ? formatDate(p.collection_end_date) : 'ongoing'}` : '-' },
            { header: 'Status', render: (p) => <StatusBadge status={p.status} label={VCOLLECT_STATUS_LABELS[p.status]} /> },
          ]}
        />
      )}

      <div className="tip-box" style={{ marginTop: 20 }}>
        <b>This is the foundation only.</b> Form Builder, ODK Central integration, enumerator/team management, submission review, and data quality are not yet built.
      </div>
    </div>
  );
}
